import { type ConnectChatbotEnableResponse, type ConnectSpaceDetail, getEnv } from "@ando/core";
import { Badge, Button, cn, toast } from "@ando/ui";
import { msg } from "@lingui/core/macro";
import { Trans, useLingui } from "@lingui/react/macro";
import { Bot, Code2, Link2, Loader2, Power } from "lucide-react";
import * as React from "react";

import {
  useDisableConnectChatbot,
  useEnableConnectChatbot,
  usePatchConnectSpace,
} from "../hooks/useConnectMutations.js";
import { ConfirmDialog, CopyableField, RailSectionHeader } from "./connectShared.js";

/**
 * ChatbotPanel — public-chatbot config for a Connect Space.
 *
 * Rendered inside the Membri tab when ``space.mode === 'chatbot'`` (the public
 * surface has no per-guest members). Enable mints a public token; the panel
 * then surfaces the PUBLIC link + a copy-paste ``<iframe>`` embed snippet (both
 * COPY-able) and a query-caps editor (daily / monthly — plain integers, NEVER
 * any currency). Disable is danger-toned + confirmed (the embed link dies).
 *
 * DRY: reuses ``useEnableConnectChatbot`` / ``useDisableConnectChatbot`` for the
 * lifecycle and ``usePatchConnectSpace`` for caps, plus the shared
 * ``ConfirmDialog``. The embed URL/snippet come straight from the enable
 * response; on a refresh we rebuild the link from ``space.public_token``.
 */

export interface ChatbotPanelProps {
  readonly space: ConnectSpaceDetail;
}

interface EmbedInfo {
  readonly embedUrl: string;
  readonly snippet: string;
}

function readEmbed(resp: ConnectChatbotEnableResponse): EmbedInfo | null {
  const embedUrl = typeof resp.embed_url === "string" ? resp.embed_url : null;
  const snippet = typeof resp.embed_snippet === "string" ? resp.embed_snippet : null;
  if (embedUrl && snippet) return { embedUrl, snippet };
  return null;
}

/** Rebuild the public embed from a token (refresh path — no enable response). */
function embedFromToken(token: string): EmbedInfo {
  const origin =
    typeof window !== "undefined" && window.location?.origin
      ? window.location.origin
      : getEnv().apiUrl;
  const embedUrl = `${origin}/connect/chatbot/${encodeURIComponent(token)}`;
  const snippet = `<iframe src="${embedUrl}" style="border:0;width:100%;height:600px" title="Ando Connect"></iframe>`;
  return { embedUrl, snippet };
}

export function ChatbotPanel({ space }: ChatbotPanelProps) {
  const { i18n } = useLingui();
  const enable = useEnableConnectChatbot();
  const disable = useDisableConnectChatbot();
  const [disableOpen, setDisableOpen] = React.useState(false);
  const [enabledEmbed, setEnabledEmbed] = React.useState<EmbedInfo | null>(null);

  const isLive = Boolean(space.public_token);
  const embed: EmbedInfo | null =
    enabledEmbed ?? (space.public_token ? embedFromToken(space.public_token) : null);

  const handleEnable = () => {
    enable.mutate(space.id, {
      onSuccess: (resp) => {
        setEnabledEmbed(readEmbed(resp));
        toast.success(i18n._(msg`Chatbot pubblicato`));
      },
      onError: () => toast.error(i18n._(msg`Impossibile pubblicare il chatbot`)),
    });
  };

  const handleDisable = () => {
    disable.mutate(space.id, {
      onSuccess: () => {
        setDisableOpen(false);
        setEnabledEmbed(null);
        toast.success(i18n._(msg`Chatbot disattivato`));
      },
      onError: () => toast.error(i18n._(msg`Impossibile disattivare il chatbot`)),
    });
  };

  return (
    <div className="flex flex-col gap-3 py-1">
      <RailSectionHeader
        title={<Trans>Chatbot pubblico</Trans>}
        action={
          isLive ? (
            <Badge variant="success" className="gap-1 py-0 text-[10px]">
              <Bot size={10} strokeWidth={2.2} aria-hidden />
              <Trans>Attivo</Trans>
            </Badge>
          ) : (
            <Badge variant="secondary" className="py-0 text-[10px]">
              <Trans>Non pubblicato</Trans>
            </Badge>
          )
        }
      />

      {!isLive ? (
        <div className="flex flex-col gap-2.5 rounded-lg border border-hairline/70 bg-white/55 px-3.5 py-3">
          <p className="text-[12px] text-ink-2">
            <Trans>
              Pubblica questo spazio come chatbot: chiunque abbia il link potrà fare domande
              limitate alle fonti condivise, senza account.
            </Trans>
          </p>
          <Button
            variant="primary"
            size="md"
            className="w-full justify-center gap-1.5"
            onClick={handleEnable}
            disabled={enable.isPending}
          >
            {enable.isPending ? (
              <Loader2 className="animate-spin" size={14} aria-hidden />
            ) : (
              <Power size={14} strokeWidth={2.2} aria-hidden />
            )}
            <Trans>Pubblica chatbot</Trans>
          </Button>
        </div>
      ) : (
        <>
          {embed ? (
            <>
              <CopyableField
                icon={<Link2 size={13} strokeWidth={2} aria-hidden />}
                label={<Trans>Link pubblico</Trans>}
                value={embed.embedUrl}
                openHref={embed.embedUrl}
                fieldAriaLabel={i18n._(msg`Link pubblico`)}
              />
              <CopyableField
                icon={<Code2 size={13} strokeWidth={2} aria-hidden />}
                label={<Trans>Codice di incorporamento</Trans>}
                value={embed.snippet}
                multiline
                fieldAriaLabel={i18n._(msg`Codice di incorporamento`)}
              />
            </>
          ) : null}

          <CapsEditor space={space} />

          <div className="mt-1 border-hairline/60 border-t pt-3">
            <Button
              variant="destructive"
              size="md"
              className="w-full justify-center gap-1.5"
              onClick={() => setDisableOpen(true)}
            >
              <Power size={14} strokeWidth={2.2} aria-hidden />
              <Trans>Disattiva chatbot</Trans>
            </Button>
          </div>
        </>
      )}

      <ConfirmDialog
        open={disableOpen}
        onOpenChange={setDisableOpen}
        title={<Trans>Disattivare il chatbot?</Trans>}
        description={
          <Trans>
            Il link pubblico e il codice di incorporamento smetteranno di funzionare immediatamente.
            Potrai ripubblicare in seguito con un nuovo link.
          </Trans>
        }
        confirmLabel={<Trans>Disattiva</Trans>}
        cancelLabel={<Trans>Annulla</Trans>}
        pending={disable.isPending}
        onConfirm={handleDisable}
      />
    </div>
  );
}
ChatbotPanel.displayName = "ChatbotPanel";

// ---------------------------------------------------------------------------
// CapsEditor — daily / monthly query caps. Plain integers, NEVER any currency.
// ---------------------------------------------------------------------------

function CapsEditor({ space }: { readonly space: ConnectSpaceDetail }) {
  const { i18n } = useLingui();
  const patch = usePatchConnectSpace();
  const [daily, setDaily] = React.useState<string>(
    space.daily_query_cap != null ? String(space.daily_query_cap) : "",
  );
  const [monthly, setMonthly] = React.useState<string>(
    space.monthly_query_cap != null ? String(space.monthly_query_cap) : "",
  );

  // Re-sync when the server value changes (after a successful patch refetch).
  React.useEffect(() => {
    setDaily(space.daily_query_cap != null ? String(space.daily_query_cap) : "");
    setMonthly(space.monthly_query_cap != null ? String(space.monthly_query_cap) : "");
  }, [space.daily_query_cap, space.monthly_query_cap]);

  const parse = (raw: string): number | null => {
    const t = raw.trim();
    if (t === "") return null;
    const n = Number.parseInt(t, 10);
    return Number.isFinite(n) && n >= 0 ? n : null;
  };

  const dailyParsed = parse(daily);
  const monthlyParsed = parse(monthly);
  const dailyInvalid = daily.trim() !== "" && dailyParsed === null;
  const monthlyInvalid = monthly.trim() !== "" && monthlyParsed === null;

  const dirty =
    dailyParsed !== (space.daily_query_cap ?? null) ||
    monthlyParsed !== (space.monthly_query_cap ?? null);

  const handleSave = () => {
    if (dailyInvalid || monthlyInvalid) return;
    patch.mutate(
      {
        spaceId: space.id,
        patch: {
          daily_query_cap: dailyParsed,
          clear_daily_cap: dailyParsed === null,
          monthly_query_cap: monthlyParsed,
          clear_monthly_cap: monthlyParsed === null,
        },
      },
      {
        onSuccess: () => toast.success(i18n._(msg`Limiti aggiornati`)),
        onError: () => toast.error(i18n._(msg`Impossibile aggiornare i limiti`)),
      },
    );
  };

  return (
    <div className="flex flex-col gap-2.5 rounded-lg border border-hairline/70 bg-white/55 px-3 py-2.5">
      <span className="font-medium text-[11.5px] text-ink-1">
        <Trans>Limiti di domande</Trans>
      </span>
      <div className="grid grid-cols-2 gap-2.5">
        <CapInput
          id="connect-cap-daily"
          label={<Trans>Al giorno</Trans>}
          value={daily}
          onChange={setDaily}
          invalid={dailyInvalid}
        />
        <CapInput
          id="connect-cap-monthly"
          label={<Trans>Al mese</Trans>}
          value={monthly}
          onChange={setMonthly}
          invalid={monthlyInvalid}
        />
      </div>
      <p className="text-[10.5px] text-ink-2">
        <Trans>Lascia vuoto per nessun limite.</Trans>
      </p>
      <Button
        variant="secondary"
        size="sm"
        className="gap-1.5 self-start"
        onClick={handleSave}
        disabled={!dirty || dailyInvalid || monthlyInvalid || patch.isPending}
      >
        {patch.isPending ? <Loader2 className="animate-spin" size={13} aria-hidden /> : null}
        <Trans>Salva limiti</Trans>
      </Button>
    </div>
  );
}

function CapInput({
  id,
  label,
  value,
  onChange,
  invalid,
}: {
  readonly id: string;
  readonly label: React.ReactNode;
  readonly value: string;
  readonly onChange: (v: string) => void;
  readonly invalid: boolean;
}) {
  return (
    <label htmlFor={id} className="flex flex-col gap-1 text-[11px] text-ink-2">
      <span className="font-medium text-ink-1">{label}</span>
      <input
        id={id}
        type="number"
        min={0}
        step={1}
        inputMode="numeric"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder="—"
        aria-invalid={invalid}
        className={cn(
          "rounded-md border bg-white px-2.5 py-1.5 text-[12px] text-ink-1 tabular-nums focus-visible:outline-none focus-visible:ring-2",
          invalid
            ? "border-rose-300 focus-visible:ring-rose-400/45"
            : "border-black/10 focus-visible:ring-ando-orange/40",
        )}
      />
    </label>
  );
}
